import { useId } from "react";

import { Skeleton } from "../../../../core/Skeleton.js";
import { Text } from "../../../../core/Text.js";
import { DropTable, type DropTableProps } from "../../../../domain/DropTable.js";
import type { TierMarker } from "../../../../domain/OddsTag.js";
import { cn } from "../../../../foundations/cn.js";
import type { BoxPageContentsContent } from "../../boxPageContent.js";

export interface BoxPageContentsProps extends BoxPageContentsContent {
  /** How the odds by tier show above the items; the layout chooses. */
  oddsView?: DropTableProps["oddsView"];
  /** An item's tier beside its chance: the dot or the shape; the layout chooses. */
  marker?: TierMarker;
  /** Cards per row at the widest: 4 beside a column, 6 full width. */
  columns?: 4 | 6;
  className?: string;
}

/**
 * Everything the box can hold, with its published odds and what a limited supply means, as a
 * DropTable; cards standing in while the box loads. A compliance surface: every item and chance
 * shows, whatever the layout.
 */
export function BoxPageContents({
  title,
  count,
  sortLabel,
  sorts,
  state,
  oddsView = "bar",
  marker,
  columns = 4,
  className,
}: BoxPageContentsProps) {
  const titleId = useId();
  if (state.status === "loading") {
    return (
      <section aria-labelledby={titleId} aria-busy className={cn("flex flex-col gap-5", className)}>
        <Text
          as="h2"
          id={titleId}
          variant="display-xl2"
          tone="primary"
          className="max-md:text-display-xl3"
        >
          {title}
        </Text>
        <div
          aria-hidden
          className={cn(
            "grid grid-cols-2 gap-3 sm:grid-cols-3",
            columns === 6 ? "lg:grid-cols-5 xl:grid-cols-6" : "xl:grid-cols-4",
          )}
        >
          {Array.from({ length: columns * 2 }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholders.
            <Skeleton key={i} className="aspect-[3/4] w-full rounded-xl" />
          ))}
        </div>
      </section>
    );
  }
  return (
    <DropTable
      title={title}
      count={count}
      sortLabel={sortLabel}
      sorts={sorts}
      odds={state.odds}
      drops={state.drops}
      notes={state.notes}
      oddsView={oddsView}
      marker={marker}
      columns={columns}
      className={className}
    />
  );
}
